/* eslint-disable @typescript-eslint/no-explicit-any */
import * as signalR from "@microsoft/signalr";
import { useAuthStore } from "@/stores/authStore";
import { isSignalRAllowed, onSignalRAllowed } from "@/lib/signalRGate";

export interface NotificationEvent {
  event: string;
  data: unknown;
}

interface NotificationData {
  id?: number;
  idNotificacion?: number;
  title?: string;
  titulo?: string;
  message?: string;
  mensaje?: string;
  icon?: string;
  icono?: string;
  bgColor?: string;
  iconColor?: string;
  order?: number;
  priority?: number;
  isPush?: boolean;
  href?: string;
  time?: string;
  tipoEnvio?: string;
}

// Nueva interfaz para geolocalización
interface GeolocationData {
  latitude: number;
  longitude: number;
  country?: string;
  city?: string;
  accuracy?: number;
}

type EventCallback = (...args: any[]) => void;
type EventMap = Map<string, EventCallback[]>;

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private connectionPromise: Promise<void> | null = null;
  public isConnected: boolean = false;
  private eventCallbacks: EventMap = new Map();
  private reconnectTimer: NodeJS.Timeout | null = null;
  private tabId: string;
  private static isInitialized = false;
  private joinedGroups: Set<string> = new Set();
  private geolocation: GeolocationData | null = null;
  private geolocationInitialized: boolean = false;
  private gracePeriodMinutes: number = 10;
  public isIntentionalDisconnect: boolean = true;

  constructor() {
    // Generar un ID único para esta pestaña/ventana - más robusto
    this.tabId = this.generateUniqueTabId();

    // Configurar listener para detectar cuando la pestaña se cierra o cambia
    window.addEventListener("beforeunload", () => {
      this.isIntentionalDisconnect = true;
      this.setGracePeriodInfo();
      this.disconnect();
    });

    // Listener para detectar cuando la pestaña pierde/gana foco
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        if (!this.isConnected && isSignalRAllowed()) {
          this.connect();
        } else if (!this.isConnected) {
          onSignalRAllowed(() => this.connect());
        }
      }
    });

    // Intentar obtener geolocalización al inicializar de forma asíncrona
    this.initializeGeolocationAsync();

    let prevToken = useAuthStore.getState().token;

    // nos suscribimos sólo con un callback
    useAuthStore.subscribe((state) => {
      const newToken = state.token;

      if (newToken && !prevToken) {
        // Acabamos de hacer login
        this.isIntentionalDisconnect = false;
        if (isSignalRAllowed()) this.connect();
        else onSignalRAllowed(() => this.connect());
      } else if (!newToken && prevToken) {
        // Acabamos de hacer logout
        this.disconnect();
      }

      // actualizamos prevToken para la próxima comparación
      prevToken = newToken;
    });
  }

  // 🆕 Agregar estos 3 métodos públicos:
  public getGracePeriodStatus(): {
    isActive: boolean;
    disconnectedAt?: Date;
    remainingMinutes?: number;
    intentional?: boolean;
  } {
    return this.getGracePeriodInfo();
  }

  public clearGracePeriod(): void {
    this.clearGracePeriodInfo();
  }

  public isInGracePeriod(): boolean {
    return this.getGracePeriodInfo().isActive;
  }

  // Generar ID único más robusto
  private generateUniqueTabId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    const sessionId =
      sessionStorage.getItem("tab-session-id") ||
      Math.random().toString(36).substring(2, 15);

    // Guardar en sessionStorage para mantener consistencia en la pestaña
    if (!sessionStorage.getItem("tab-session-id")) {
      sessionStorage.setItem("tab-session-id", sessionId);
    }

    return `tab_${timestamp}_${sessionId}_${random}`;
  }
  // 🆕 Agregar estos 4 métodos después de `generateUniqueTabId()`:
  private getCurrentUserId(): string | null {
    const token = useAuthStore.getState().token;
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.id_usuario || payload.sub || payload.nameid;
      } catch (error) {
        console.error("Error al decodificar token:", error);
      }
    }
    return null;
  }

  private setGracePeriodInfo(): void {
    const gracePeriodInfo = {
      disconnectedAt: new Date().toISOString(),
      tabId: this.tabId,
      userId: this.getCurrentUserId(),
      intentional: this.isIntentionalDisconnect,
    };

    localStorage.setItem(
      "signalr_grace_period",
      JSON.stringify(gracePeriodInfo)
    );
  }

  private getGracePeriodInfo(): {
    isActive: boolean;
    disconnectedAt?: Date;
    remainingMinutes?: number;
    intentional?: boolean;
  } {
    try {
      const storedInfo = localStorage.getItem("signalr_grace_period");
      if (!storedInfo) return { isActive: false };

      const gracePeriodInfo = JSON.parse(storedInfo);
      const disconnectedAt = new Date(gracePeriodInfo.disconnectedAt);
      const now = new Date();
      const minutesElapsed =
        (now.getTime() - disconnectedAt.getTime()) / (1000 * 60);

      const isActive = minutesElapsed < this.gracePeriodMinutes;
      const remainingMinutes = isActive
        ? this.gracePeriodMinutes - minutesElapsed
        : 0;

      return {
        isActive,
        disconnectedAt,
        remainingMinutes,
        intentional: gracePeriodInfo.intentional || false,
      };
    } catch (error) {
      console.error("Error al obtener info del grace period:", error);
      return { isActive: false };
    }
  }

  private clearGracePeriodInfo(): void {
    localStorage.removeItem("signalr_grace_period");
    this.isIntentionalDisconnect = false;
  }

  // Singleton pattern para evitar múltiples instancias
  async initialize() {
    if (SignalRService.isInitialized) {
      return;
    }

    try {
      if (this.connection && this.isConnected) {
        return true;
      }

      // Si hay una conexión en progreso, esperar a que termine
      if (this.connectionPromise) {
        await this.connectionPromise;
        return this.isConnected;
      }

      const gracePeriodInfo = this.getGracePeriodInfo();
      const gracePeriodActive = gracePeriodInfo.isActive;
      if (gracePeriodActive) {
        // Grace period active
      }
      // Esperar un poco a que la geolocalización se inicialice
      await this.waitForGeolocationInit();

      const apiUrl = import.meta.env.VITE_API_URL || "";

      const params = new URLSearchParams({
        tabId: this.tabId,
        gracePeriod: gracePeriodActive.toString(),
        ...(this.geolocation
          ? {
              latitude: this.geolocation.latitude.toString(),
              longitude: this.geolocation.longitude.toString(),
              country: this.geolocation.country || "",
              city: this.geolocation.city || "",
            }
          : {}),
      });
      const hubUrl = `${apiUrl}/signalrhub?${params.toString()}`;
      const token = useAuthStore.getState().token;
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => {
            const currentToken = token || useAuthStore.getState().token;
            if (!currentToken) {
              console.warn("No token available for SignalR connection");
              return "";
            }

            // Check if token is expired
            const authState = useAuthStore.getState();
            if (authState.expiresAt) {
              const expiresAt = new Date(authState.expiresAt);
              const now = new Date();
              if (now >= expiresAt) {
                console.warn("Token is expired for SignalR connection");
                return "";
              }
            }

            return currentToken;
          },
          skipNegotiation: false,
          transport:
            signalR.HttpTransportType.WebSockets |
            signalR.HttpTransportType.ServerSentEvents |
            signalR.HttpTransportType.LongPolling,
          // NO pongas headers aquí, no sirven para WebSockets
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (gracePeriodActive) {
              return 2000; // 2 segundos
            }
            if (retryContext.elapsedMilliseconds < 60000) {
              return 5000;
            } else {
              return 30000;
            }
          },
        })

        .configureLogging(signalR.LogLevel.Information)
        .build();

      await this.connection.start();
      SignalRService.isInitialized = true;
    } catch (error) {
      console.error("Error al inicializar SignalR:", error);
    }
  }
  // Inicializar geolocalización de forma asíncrona sin bloquear
  private initializeGeolocationAsync(): void {
    // No usar await aquí para no bloquear el constructor
    this.initializeGeolocation()
      .then(() => {
        this.geolocationInitialized = true;
      })
      .catch((error) => {
        console.warn("No se pudo obtener geolocalización:", error);
        this.geolocationInitialized = true; // Marcar como inicializada aunque haya fallado
      });
  }

  // Inicializar geolocalización
  private async initializeGeolocation(): Promise<void> {
    try {
      if (!navigator.geolocation) {
        console.warn("Geolocalización no soportada por este navegador");
        return;
      }

      // Verificar permisos primero
      if ("permissions" in navigator) {
        const permission = await navigator.permissions.query({
          name: "geolocation",
        });
        if (permission.state === "denied") {
          console.warn("Permisos de geolocalización denegados");
          return;
        }
      }

      const position = await this.getCurrentPosition();
      this.geolocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      };

      // Obtener información adicional de ubicación
      const locationInfo = await this.getLocationInfo(
        this.geolocation.latitude,
        this.geolocation.longitude
      );

      if (locationInfo) {
        this.geolocation.country = locationInfo.country;
        this.geolocation.city = locationInfo.city;
      }
    } catch (error) {
      console.warn("Error al obtener geolocalización:", error);
      // No es crítico, continuar sin geolocalización
    }
  }

  // Obtener posición actual con mejores opciones
  private getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      const options: PositionOptions = {
        enableHighAccuracy: false, // Cambiar a false para ser menos agresivo
        timeout: 15000, // Aumentar timeout
        maximumAge: 600000, // 10 minutos
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position);
        },
        (error) => {
          console.warn("Error de geolocalización:", error.message);
          reject(error);
        },
        options
      );
    });
  }

  // Obtener información de ubicación usando API externa
  private async getLocationInfo(
    latitude: number,
    longitude: number
  ): Promise<any> {
    try {
      // Usar API más robusta con timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=es`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        return {
          country: data.countryName,
          city: data.city || data.locality || data.principalSubdivision,
          region: data.principalSubdivision,
          countryCode: data.countryCode,
        };
      }
    } catch (error) {
      console.warn("Error al obtener información de ubicación:", error);
    }
    return null;
  }

  // Esperar a que la geolocalización esté inicializada
  private async waitForGeolocationInit(): Promise<void> {
    if (this.geolocationInitialized) return;

    // Esperar hasta 3 segundos máximo
    const maxWait = 3000;
    const startTime = Date.now();

    while (!this.geolocationInitialized && Date.now() - startTime < maxWait) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  // Conectar al hub
  async connect(token?: string): Promise<boolean> {
    try {
      // Si ya hay una conexión activa, no crear otra
      if (this.connection && this.isConnected) {
        return true;
      }

      // Si hay una conexión en progreso, esperar a que termine
      if (this.connectionPromise) {
        await this.connectionPromise;
        return this.isConnected;
      }

      const gracePeriodInfo = this.getGracePeriodInfo();
      const gracePeriodActive = gracePeriodInfo.isActive;
      if (gracePeriodActive) {
        // Grace period active
      }
      // Esperar un poco a que la geolocalización se inicialice
      await this.waitForGeolocationInit();

      const apiUrl = import.meta.env.VITE_API_URL || "";

      const params = new URLSearchParams({
        tabId: this.tabId,
        gracePeriod: gracePeriodActive.toString(),
        ...(this.geolocation
          ? {
              latitude: this.geolocation.latitude.toString(),
              longitude: this.geolocation.longitude.toString(),
              country: this.geolocation.country || "",
              city: this.geolocation.city || "",
            }
          : {}),
      });
      const hubUrl = `${apiUrl}/signalrhub?${params.toString()}`;

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => {
            const currentToken = token || useAuthStore.getState().token;
            if (!currentToken) {
              console.warn("No token available for SignalR connection");
              return "";
            }

            // Check if token is expired
            const authState = useAuthStore.getState();
            if (authState.expiresAt) {
              const expiresAt = new Date(authState.expiresAt);
              const now = new Date();
              if (now >= expiresAt) {
                console.warn("Token is expired for SignalR connection");
                return "";
              }
            }

            return currentToken;
          },
          skipNegotiation: false,
          transport:
            signalR.HttpTransportType.WebSockets |
            signalR.HttpTransportType.ServerSentEvents |
            signalR.HttpTransportType.LongPolling,
          // NO pongas headers aquí, no sirven para WebSockets
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (gracePeriodActive) {
              return 2000; // 2 segundos
            }
            if (retryContext.elapsedMilliseconds < 60000) {
              return 5000;
            } else {
              return 30000;
            }
          },
        })

        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Configurar listeners de eventos del hub
      this.setupHubListeners();

      // Configurar listeners de conexión
      this.setupConnectionListeners(token);

      // Iniciar conexión
      this.connectionPromise = this.connection
        .start()
        .then(async () => {
          this.isConnected = true;
          this.joinUserGroup();

          // Manual reconnects (scheduleReconnect) build a brand-new connection that is
          // not in any hub group yet; re-invoke JoinGroup for every group we were in,
          // same as onreconnected() does for automatic reconnects.
          const groupsToRejoin = Array.from(this.joinedGroups);
          this.joinedGroups.clear();
          for (const group of groupsToRejoin) {
            try {
              await this.connection!.invoke("JoinGroup", group);
              this.joinedGroups.add(group);
            } catch (error) {
              console.error(`Error al reunirse al grupo ${group}:`, error);
            }
          }

          if (gracePeriodActive) {
            this.clearGracePeriodInfo();
          }
          this.notifyCallbacks("connected", {
            connectionId: this.connection?.connectionId,
            tabId: this.tabId,
            geolocation: this.geolocation,
            gracePeriodUsed: gracePeriodActive, // 🆕 Agregar esta línea
          });
          this.connectionPromise = null;

          // Si tenemos geolocalización, actualizar ubicación en el servidor después de conectar,
          // pero asegurando que el estado de la conexión sea 'Connected'
          if (this.geolocation) {
            // Esperar a que la conexión esté realmente en estado Connected
            if (
              this.connection &&
              this.connection.state !== signalR.HubConnectionState.Connected
            ) {
              // Esperar hasta que esté en estado Connected con un pequeño loop
              let retries = 10;
              while (
                (this.connection.state as any) !==
                  signalR.HubConnectionState.Connected &&
                retries > 0
              ) {
                await new Promise((resolve) => setTimeout(resolve, 200));
                retries--;
              }
            }
            if (
              this.connection &&
              this.connection.state === signalR.HubConnectionState.Connected
            ) {
              try {
                await this.updateMyLocation(
                  this.geolocation.latitude,
                  this.geolocation.longitude,
                  this.geolocation.country,
                  this.geolocation.city
                );
              } catch (error) {
                // Si ocurre error aquí, solo loguear
                console.error(
                  "Error al actualizar ubicación tras conectar:",
                  error
                );
              }
            } else {
              // Si no se logró conectar tras los intentos, loguear advertencia
              console.warn(
                "No se pudo actualizar la ubicación porque la conexión no está en estado Connected."
              );
            }
          }
        })
        .catch((error) => {
          console.error("❌ Error al conectar con el hub:", error);
          this.isConnected = false;
          this.connectionPromise = null;

          // Check if it's an authentication error
          if (
            (error.message && error.message.includes("Unauthorized")) ||
            (error.message && error.message.includes("401"))
          ) {
            console.error(
              "❌ Error de autenticación en SignalR - posible token expirado o inválido"
            );
            // Notify callbacks about authentication failure
            this.notifyCallbacks("authenticationFailed", error);

            // Don't retry on auth errors - let the app handle re-authentication
            return;
          }

          this.scheduleReconnect(token);
          throw error;
        });

      await this.connectionPromise;
      return this.isConnected;
    } catch (error) {
      console.error("Error al conectar con el hub:", error);
      this.isConnected = false;

      // Check if it's an authentication error
      if (
        error instanceof Error &&
        (error.message.includes("Unauthorized") ||
          error.message.includes("401"))
      ) {
        console.error(
          "❌ Error de autenticación en SignalR - posible token expirado o inválido"
        );
        this.notifyCallbacks("authenticationFailed", error);
        return false;
      }

      this.scheduleReconnect(token);
      return false;
    }
  }

  // Programar reconexión manual
  private scheduleReconnect(token?: string) {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      this.connect(token);
    }, 5000);
  }

  // Configurar listeners de eventos del hub
  private setupHubListeners() {
    if (!this.connection) return;

    //agregar mas metodos aqui para la comunicacion  server-client
    this.connection.on(
      "TestSignalRClient_Server",
      (payload: { mensaje: string; from: string }) => {
        console.log("Evento TestSignalR (desde Hub):", payload);
        this.notifyCallbacks("TestSignalR", payload);
      }
    );

    this.connection.on("reparticion-27", (payload: any) => {
      this.notifyCallbacks("reparticion-27", payload);
    });
    this.connection.on("reparticion-28", (payload: any) => {
      this.notifyCallbacks("reparticion-28", payload);
    });
    this.connection.on("reparticion-29", (payload: any) => {
      this.notifyCallbacks("reparticion-29", payload);
    });
    this.connection.on("reparticion-30", (payload: any) => {
      this.notifyCallbacks("reparticion-30", payload);
    });
    this.connection.on("reparticion-31", (payload: any) => {
      this.notifyCallbacks("reparticion-31", payload);
    });
    this.connection.on("reparticion-32", (payload: any) => {
      this.notifyCallbacks("reparticion-32", payload);
    });

    this.connection.on("ServerClientReparticion27", (payload: any) => {
      this.notifyCallbacks("ServerClientReparticion27", payload);
    });
    this.connection.on("ServerClientReparticion28", (payload: any) => {
      this.notifyCallbacks("ServerClientReparticion28", payload);
    });
    this.connection.on("ServerClientReparticion29", (payload: any) => {
      this.notifyCallbacks("ServerClientReparticion29", payload);
    });
    this.connection.on("ServerClientReparticion30", (payload: any) => {
      this.notifyCallbacks("ServerClientReparticion30", payload);
    });
    this.connection.on("ServerClientReparticion31", (payload: any) => {
      this.notifyCallbacks("ServerClientReparticion31", payload);
    });
    this.connection.on("ServerClientReparticion32", (payload: any) => {
      this.notifyCallbacks("ServerClientReparticion32", payload);
    });
    this.connection.on("ServerClientReparticionMaster", (payload: any) => {
      this.notifyCallbacks("ServerClientReparticionMaster", payload);
    });
    this.connection.on("ServerClientReparticionMasterRecuperacion", (payload: any) => {
      this.notifyCallbacks("ServerClientReparticionMasterRecuperacion", payload);
    });

    this.connection.on("ClientServerReparticionMaster", (payload: any) => {
      this.notifyCallbacks("ClientServerReparticionMaster", payload);
    });

    // Recibe mensajes disparados desde tu endpoint API
    this.connection.on(
      "TestSignalRServer_Client",
      (payload: { mensaje: string; status: string }) => {
        this.notifyCallbacks("TestSignalRServer_Client", payload);
      }
    );

    // Eventos de notificaciones existentes
    this.connection.on("NewNotification", (notification: NotificationData) => {
      this.notifyCallbacks("NewNotification", notification);

      // Mostrar notificación push si está habilitada
      if (
        notification.isPush &&
        "Notification" in window &&
        Notification.permission === "granted"
      ) {
        new Notification(notification.title || notification.titulo || "", {
          body: notification.message || notification.mensaje || "",
          icon: "/logo.png",
          tag: `notification-${notification.id || notification.idNotificacion}`,
          requireInteraction: (notification.priority || 0) >= 2,
        });
      }
    });

    this.connection.on("NotificationRead", (notificationId: number) => {
      this.notifyCallbacks("NotificationRead", notificationId);
    });

    this.connection.on("AllNotificationsRead", () => {
      this.notifyCallbacks("AllNotificationsRead", null);
    });

    this.connection.on("UnreadCount", (count: number) => {
      this.notifyCallbacks("UnreadCount", count);
    });

    this.connection.on("Connected", (data: any) => {
      if (data.gracePeriodActive) {
        // Grace period active
      }
      this.notifyCallbacks("Connected", data);
    });

    this.connection.on("JoinedGroup", (groupName: string) => {
      this.notifyCallbacks("JoinedGroup", groupName);
    });

    this.connection.on("leftgroup", () => {});
    this.connection.on("LeftGroup", () => {});

    this.connection.on("UpdateTable", (notificationId: any, userId: any) => {
      this.notifyCallbacks("UpdateTable", notificationId, userId);
    });

    // Nuevos eventos para conexiones
    this.connection.on("UserConnected", (data: any) => {
      this.notifyCallbacks("UserConnected", data);
    });

    this.connection.on("UserDisconnected", (data: any) => {
      this.notifyCallbacks("UserDisconnected", data);
    });

    this.connection.on("SuspiciousConnection", (data: any) => {
      console.warn("⚠️ Conexión sospechosa detectada:", data);
      this.notifyCallbacks("SuspiciousConnection", data);

      // Mostrar alerta visual
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("⚠️ Conexión Sospechosa", {
          body: data.description,
          icon: "/logo.png",
          tag: "suspicious-connection",
          requireInteraction: true,
        });
      }
    });

    this.connection.on("ConnectedUsersList", (users: any[]) => {
      this.notifyCallbacks("ConnectedUsersList", users);
    });

    this.connection.on("ConnectionsMapData", (mapData: any[]) => {
      this.notifyCallbacks("ConnectionsMapData", mapData);
    });

    this.connection.on("DeviceStatistics", (stats: any) => {
      this.notifyCallbacks("DeviceStatistics", stats);
    });

    this.connection.on("GeographicStatistics", (stats: any) => {
      this.notifyCallbacks("GeographicStatistics", stats);
    });

    this.connection.on("MySessionHistory", (history: any) => {
      this.notifyCallbacks("MySessionHistory", history);
    });

    this.connection.on("LocationUpdated", (data: any) => {
      this.notifyCallbacks("LocationUpdated", data);
    });

    this.connection.on("ConnectionStatus", (status: any) => {
      this.notifyCallbacks("ConnectionStatus", status);
    });

    this.connection.on("Error", (error: string) => {
      console.error("❌ Error desde el servidor:", error);
      this.notifyCallbacks("Error", error);
    });
    this.connection.on("GracePeriodStatus", (data: any) => {
      this.notifyCallbacks("GracePeriodStatus", data);
    });

    // Nuevo evento para actualizaciones específicas de bloqueo de prospectos
    this.connection.on("ProspectoBloqueoActualizado", (data: any) => {
      this.notifyCallbacks("ProspectoBloqueoActualizado", data);
    });

    // SignalR JS client matches hub method names case-insensitively, so a single
    // registration also receives the server's lowercase "wa_newmessage" sends.
    // Registering both casings would dispatch every event twice.
    this.connection.on("WA_NewMessage", (...args: any[]) => {
      this.notifyCallbacks("WA_NewMessage", ...args);
    });

    this.connection.on("WA_ConversationUpdated", (...args: any[]) => {
      this.notifyCallbacks("WA_ConversationUpdated", ...args);
    });

    this.connection.on("WA_MessageStatusUpdated", (...args: any[]) => {
      this.notifyCallbacks("WA_MessageStatusUpdated", ...args);
    });
  }

  // Configurar listeners de conexión
  private setupConnectionListeners(token?: string) {
    if (!this.connection) return;

    this.connection.onreconnecting((error) => {
      this.isConnected = false;
      this.notifyCallbacks("reconnecting", error);
    });

    this.connection.onreconnected(async (connectionId) => {
      this.isConnected = true;
      this.joinUserGroup();

      const groupsToRejoin = Array.from(this.joinedGroups);
      this.joinedGroups.clear();
      for (const group of groupsToRejoin) {
        try {
          await this.connection!.invoke("JoinGroup", group);
          this.joinedGroups.add(group);
        } catch (error) {
          console.error(`Error al reunirse al grupo ${group}:`, error);
        }
      }

      this.notifyCallbacks("reconnected", connectionId);
    });

    this.connection.onclose((error) => {
      this.isConnected = false;
      this.notifyCallbacks("disconnected", error);

      if (!this.isIntentionalDisconnect) {
        this.setGracePeriodInfo();
        // Solo intentar reconectar si no fue una desconexión intencional
        if (error) {
          this.scheduleReconnect(token);
        }
      } else {
        // intentional disconnect
      }
    });
  }

  // Unirse al grupo de usuario
  private async joinUserGroup(): Promise<void> {
    if (!this.connection || !this.isConnected) return;

    try {
      // No necesitamos hacer nada aquí porque el backend automáticamente
      // agrega al usuario a sus grupos en OnConnectedAsync()
    } catch (error) {
      console.error("Error al unirse al grupo de usuario:", error);
    }
  }

  // Sistema de callbacks dinámicos.
  // Hub events must be wired in setupHubListeners(); registering connection.on here
  // duplicated dispatches (hub method names are case-insensitive) and the off() on
  // cleanup removed the setupHubListeners handler for everyone else.
  registerCallback(eventName: string, callback: EventCallback): () => void {
    if (!this.eventCallbacks.has(eventName)) {
      this.eventCallbacks.set(eventName, []);
    }

    const callbacks = this.eventCallbacks.get(eventName)!;
    callbacks.push(callback);

    // Retornar función para desregistrar el callback
    return () => {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
      if (callbacks.length === 0) {
        this.eventCallbacks.delete(eventName);
      }
    };
  }

  // Notificar a todos los callbacks registrados para un evento
  private notifyCallbacks(eventName: string, ...args: any[]) {
    const callbacks = this.eventCallbacks.get(eventName);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error en callback para evento ${eventName}:`, error);
        }
      });
    }
  }

  // ===== MÉTODOS EXISTENTES DE NOTIFICACIONES =====
  async markAsRead(notificationId: number): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("MarkAsRead", notificationId);
        return true;
      } catch (error) {
        console.error("Error al marcar como leída:", error);
        return false;
      }
    }
    return false;
  }

  async markAllAsRead(): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("MarkAllAsRead");
        return true;
      } catch (error) {
        console.error("Error al marcar todas como leídas:", error);
        return false;
      }
    }
    return false;
  }

  async getUnreadCount(): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("GetUnreadCount");
        return true;
      } catch (error) {
        console.error("Error al obtener contador:", error);
        return false;
      }
    }
    return false;
  }

  // ===== NUEVOS MÉTODOS PARA CONEXIONES =====

  // Obtener usuarios conectados
  async getConnectedUsers(): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("GetConnectedUsers");
        return true;
      } catch (error) {
        console.error("Error al obtener usuarios conectados:", error);
        return false;
      }
    }
    return false;
  }

  // Obtener mapa de conexiones
  async getConnectionsMap(): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("GetConnectionsMap");
        return true;
      } catch (error) {
        console.error("Error al obtener mapa de conexiones:", error);
        return false;
      }
    }
    return false;
  }

  // Obtener estadísticas de dispositivos
  async getDeviceStatistics(fromDate?: Date, toDate?: Date): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("GetDeviceStatistics", fromDate, toDate);
        return true;
      } catch (error) {
        console.error("Error al obtener estadísticas de dispositivos:", error);
        return false;
      }
    }
    return false;
  }

  // Obtener estadísticas geográficas
  async getGeographicStatistics(
    fromDate?: Date,
    toDate?: Date
  ): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke(
          "GetGeographicStatistics",
          fromDate,
          toDate
        );
        return true;
      } catch (error) {
        console.error("Error al obtener estadísticas geográficas:", error);
        return false;
      }
    }
    return false;
  }

  // Obtener historial de sesiones del usuario actual
  async getMySessionHistory(
    fromDate?: Date,
    toDate?: Date,
    page = 1,
    pageSize = 20
  ): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke(
          "GetMySessionHistory",
          fromDate,
          toDate,
          page,
          pageSize
        );
        return true;
      } catch (error) {
        console.error("Error al obtener historial de sesiones:", error);
        return false;
      }
    }
    return false;
  }

  // Actualizar ubicación
  async updateMyLocation(
    latitude: number,
    longitude: number,
    country?: string,
    city?: string
  ): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke(
          "UpdateMyLocation",
          latitude,
          longitude,
          country,
          city
        );
        return true;
      } catch (error) {
        console.error("Error al actualizar ubicación:", error);
        return false;
      }
    }
    return false;
  }

  // Verificar estado de conexión
  async checkConnectionStatus(): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("CheckConnectionStatus");
        return true;
      } catch (error) {
        console.error("Error al verificar estado:", error);
        return false;
      }
    }
    return false;
  }

  // ===== UTILIDADES =====

  // Actualizar geolocalización manualmente
  async refreshGeolocation(): Promise<boolean> {
    try {
      await this.initializeGeolocation();
      if (
        this.connection &&
        this.connection.state === signalR.HubConnectionState.Connected
      ) {
        await this.updateMyLocation(
          this.geolocation!.latitude,
          this.geolocation!.longitude,
          this.geolocation!.country,
          this.geolocation!.city
        );
      } else {
        // Esperar y reintentar cuando esté conectada
        this.connection?.on("Connected", () => {
          this.updateMyLocation(
            this.geolocation!.latitude,
            this.geolocation!.longitude,
            this.geolocation!.country,
            this.geolocation!.city
          );
        });
      }
      return true;
    } catch (error) {
      console.error("Error al actualizar geolocalización:", error);
      return false;
    }
  }

  // Obtener información de geolocalización actual
  getCurrentGeolocation(): GeolocationData | null {
    return this.geolocation;
  }

  // Solicitar permisos de geolocalización
  async requestGeolocationPermission(): Promise<boolean> {
    try {
      if (!navigator.geolocation) {
        console.warn("Geolocalización no soportada");
        return false;
      }

      // Intentar obtener la posición (esto solicitará permisos si es necesario)
      await this.getCurrentPosition();
      await this.initializeGeolocation();

      // Si estamos conectados, actualizar en el servidor
      if (this.isConnected && this.geolocation) {
        await this.updateMyLocation(
          this.geolocation.latitude,
          this.geolocation.longitude,
          this.geolocation.country,
          this.geolocation.city
        );
      }

      return true;
    } catch (error) {
      console.error("Error al solicitar permisos de geolocalización:", error);
      return false;
    }
  }

  // Obtener información del Tab ID
  getTabId(): string {
    return this.tabId;
  }

  // ===== MÉTODOS DE GRUPOS =====
  async joinGroup(groupName: string): Promise<boolean> {
    try {
      if (this.joinedGroups.has(groupName)) {
        console.log(`[SignalR] joinGroup("${groupName}") — ya en joinedGroups, saltando`);
        return true;
      }

      if (!this.isConnected) {
        console.log(`[SignalR] joinGroup("${groupName}") — no conectado, llamando connect()...`);
        await this.connect();
      }

      if (this.connection && this.isConnected) {
        console.log(`[SignalR] joinGroup("${groupName}") — invocando JoinGroup...`);
        await this.connection.invoke("JoinGroup", groupName);
        this.joinedGroups.add(groupName);
        console.log(`[SignalR] joinGroup("${groupName}") — OK, unido al grupo`);
        return true;
      } else {
        console.warn(
          `[SignalR] joinGroup("${groupName}") — conexión no disponible (connected=${this.isConnected})`
        );
        return false;
      }
    } catch (error) {
      console.error(`Error al unirse al grupo ${groupName}:`, error);
      return false;
    }
  }

  async leaveGroup(groupName: string): Promise<boolean> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("LeaveGroup", groupName);
        this.joinedGroups.delete(groupName);
        return true;
      } catch (error) {
        console.error("Error al salir del grupo:", error);
        return false;
      }
    }
    return false;
  }

  // ===== MÉTODOS DE LIMPIEZA Y COMPATIBILIDAD =====

  // Desconectar
  async disconnect(isLogout: boolean = false): Promise<void> {
    // 🆕 Marcar como desconexión intencional ANTES de cualquier operación
    this.isIntentionalDisconnect = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.connection) {
      try {
        // Si es logout, notificar al servidor antes de desconectar
        if (isLogout) {
          try {
            await this.connection.invoke("NotifyLogout");
          } catch (error) {
            console.error("Error al notificar logout:", error);
          }
        }

        await this.connection.stop();
      } catch (error) {
        console.error("Error al desconectar:", error);
      }

      this.connection = null;
      this.isConnected = false;
      this.connectionPromise = null;
      this.joinedGroups.clear();
    }
  }

  // Verificar si hay soporte para notificaciones push
  async checkPushNotificationSupport(): Promise<boolean> {
    if (!("Notification" in window)) {
      return false;
    }

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }

    return false;
  }

  // Obtener estado de conexión
  getConnectionState(): signalR.HubConnectionState {
    return this.connection?.state || signalR.HubConnectionState.Disconnected;
  }

  // Métodos de compatibilidad con el SignalRService anterior
  async start(): Promise<void> {
    await this.connect();
  }

  async stop(): Promise<void> {
    await this.disconnect();
  }

  // Métodos específicos de compatibilidad (deprecated, usar registerCallback en su lugar)
  onNotificationRead(callback: (notificationId: number) => void): void {
    this.registerCallback("NotificationRead", (data: unknown) => {
      callback(data as number);
    });
  }

  onAllNotificationsRead(callback: () => void): void {
    this.registerCallback("AllNotificationsRead", () => {
      callback();
    });
  }

  offNotificationRead(): void {
    this.eventCallbacks.delete("NotificationRead");
  }

  offAllNotificationsRead(): void {
    this.eventCallbacks.delete("AllNotificationsRead");
  }

  // Método para compatibilidad con NotificationService (suscripción general)
  subscribe(callback: (event: string, data: unknown) => void): () => void {
    const unsubscribers: (() => void)[] = [];

    // Registrar para todos los eventos conocidos
    const events = [
      "NewNotification",
      "NotificationRead",
      "AllNotificationsRead",
      "UnreadCount",
      "Connected",
      "JoinedGroup",
      "UpdateTable",
      "UserConnected",
      "UserDisconnected",
      "SuspiciousConnection",
      "ConnectedUsersList",
      "ConnectionsMapData",
      "DeviceStatistics",
      "GeographicStatistics",
      "MySessionHistory",
      "LocationUpdated",
      "ConnectionStatus",
      "GracePeriodStatus",
      "ProspectoBloqueoActualizado",
      "Error",
      "connected",
      "reconnecting",
      "reconnected",
      "disconnected",
      "authenticationFailed",
    ];

    events.forEach((eventName) => {
      const unsubscribe = this.registerCallback(eventName, (data) => {
        // Mapear nombres de eventos para compatibilidad
        let mappedEvent = eventName;
        if (eventName === "NewNotification") mappedEvent = "new";
        if (eventName === "NotificationRead") mappedEvent = "read";
        if (eventName === "AllNotificationsRead") mappedEvent = "allRead";
        if (eventName === "UnreadCount") mappedEvent = "unreadCount";
        if (eventName === "UpdateTable") mappedEvent = "updateTable";
        if (eventName === "GracePeriodStatus")
          mappedEvent = "gracePeriodStatus";

        callback(mappedEvent, data);
      });
      unsubscribers.push(unsubscribe);
    });

    // Retornar función que desregistra todos los callbacks
    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }
}

let instance: SignalRService | null = null;

// Exportar una función que retorne la instancia
export const getSignalRService = () => {
  if (!instance) {
    instance = new SignalRService();
  }
  return instance;
};
export const removeSignalRService = () => {
  if (instance) {
    instance.disconnect();
    instance = null;
  }
};

export const signalRService = getSignalRService();
